import React from 'react';
import {
  Check,
  Sparkles,
  Shield,
  Users,
  Package,
  Zap,
  FileText,
  CreditCard,
  Link2,
  Code,
  QrCode,
  Calendar,
  CalendarCheck,
  Tag,
  Settings,
  UserCheck,
  Star,
  Store,
  PenTool,
  TicketCheck,
  Gift,
  LayoutDashboard,
  TrendingUp,
  PieChart,
  Receipt,
  UserPlus,
  Bell,
  Sliders,
  Target,
  Rocket,
  ExternalLink,
} from 'lucide-react';
import { BeaconIcon } from '../BeaconIcon';

export const EXTENSION_URL = 'https://chromewebstore.google.com/detail/bookingflows-beacon-tende/lomkgfganpkdkeeeipjdngkcmhbbahkh';

/* ─────────────────────────────────────────────────────────────────────────
 * Primitives (shared with HelpPage shell)
 * ────────────────────────────────────────────────────────────────────── */

export const Bullet = ({ children }) => (
  <li className="flex gap-2.5 text-sm text-gray-700">
    <Check className="w-4 h-4 mt-0.5 text-emerald-600 flex-shrink-0" />
    <span className="leading-relaxed">{children}</span>
  </li>
);

export const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="mb-6">
    <div className="flex items-center gap-3 mb-1.5">
      <div className="w-9 h-9 rounded-lg bg-gray-100 text-gray-700 flex items-center justify-center">
        <Icon className="w-5 h-5" />
      </div>
      <h2 className="text-xl font-semibold text-gray-900 tracking-tight">{title}</h2>
    </div>
    {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
  </div>
);

export const Card = ({ children, className = '' }) => (
  <div className={`rounded-xl border border-gray-200 bg-white p-5 ${className}`}>{children}</div>
);

export const PathHint = ({ children }) => (
  <div className="mt-4 text-xs text-gray-500">
    <span className="font-medium text-gray-700">Where:</span> {children}
  </div>
);

/* ─────────────────────────────────────────────────────────────────────────
 * Tab content sections — keyed by dashboard nav `id` for 1:1 mapping
 * ────────────────────────────────────────────────────────────────────── */

const HomeContent = () => (
  <div>
    <SectionHeader icon={LayoutDashboard} title="Dashboard" subtitle="Today's operations at a glance." />
    <Card>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        Your home view. Snapshot of today's bookings, revenue, leads waiting on
        follow-up, and the upcoming schedule. Use the date filter to zoom into a week,
        month, or season.
      </p>
      <ul className="space-y-2">
        <Bullet>Net revenue + completed/upcoming bookings</Bullet>
        <Bullet>Revenue chart with monthly breakdown</Bullet>
        <Bullet>New leads waiting on a reply</Bullet>
        <Bullet>Upcoming schedule with quick check-in</Bullet>
      </ul>
    </Card>
  </div>
);

const RolesContent = () => (
  <div>
    <SectionHeader icon={Shield} title="Roles" subtitle="The backbone of role-based access." />
    <Card>
      <div className="text-sm font-medium text-gray-900 mb-2">What a role is</div>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        A <strong>Role</strong> is a position at your company (Captain, Guide,
        Front-desk). Each role carries its own permissions — what dashboard tabs are
        visible, and inside each tab whether the member can <strong>read</strong>,{' '}
        <strong>create</strong>, <strong>edit</strong>, or <strong>delete</strong>.
      </p>
      <ul className="space-y-2">
        <Bullet>Per-tab read / create / edit / delete toggles</Bullet>
        <Bullet>Pay rates per role (hourly, per-booking, or salary)</Bullet>
        <Bullet>Bind a role to specific services so it only sees relevant trips</Bullet>
        <Bullet>Roles also gate which assets a member can operate — set them early</Bullet>
        <Bullet>Change a role and every assigned team member updates instantly</Bullet>
      </ul>
      <PathHint>Deck → Roles</PathHint>
    </Card>
  </div>
);

const TeamContent = () => (
  <div>
    <SectionHeader icon={Users} title="Team" subtitle="The people behind the bookings." />
    <Card>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        Team members are the staff who help run trips. Each one gets their own login,
        sees only what their role permits, and can claim trips, log hours, and check
        guests in.
      </p>
      <ul className="space-y-2">
        <Bullet>Email or phone-based invite — they set their own password</Bullet>
        <Bullet>Optional passkey for fast, secure login</Bullet>
        <Bullet>Time-clock with GPS for hourly staff</Bullet>
        <Bullet>Approval queue for self-signup requests</Bullet>
      </ul>
      <PathHint>Deck → Team</PathHint>
    </Card>
  </div>
);

const AssetsContent = () => (
  <div>
    <SectionHeader icon={Package} title="Assets" subtitle="The things you book against." />
    <Card>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        Anything with finite availability — a boat, a room, a vehicle. Bookings schedule
        against assets so the system never double-books. Assign assets early: capacity,
        crew rules, and your HELM earnings reports all key off which asset ran which trip.
      </p>
      <ul className="space-y-2">
        <Bullet>Per-asset capacity and overbooking prevention</Bullet>
        <Bullet>Link assets to specific services (e.g. only 24ft boats run this tour)</Bullet>
        <Bullet>Require specific roles to operate an asset — controls trip assignment</Bullet>
        <Bullet>Photos, specs, registration numbers, maintenance notes</Bullet>
        <Bullet>Per-asset availability windows and blackout dates</Bullet>
      </ul>
      <PathHint>Deck → Assets</PathHint>
    </Card>
  </div>
);

const ServicesContent = () => (
  <div>
    <SectionHeader icon={Zap} title="Services" subtitle="What customers actually book." />
    <Card>
      <div className="text-sm font-medium text-gray-900 mb-2">Two capacity modes</div>
      <div className="grid sm:grid-cols-2 gap-3 mb-2">
        <div className="rounded-lg bg-gray-50 p-4">
          <div className="text-sm font-semibold text-gray-900 mb-1">Per-ticket</div>
          <p className="text-xs text-gray-600 leading-relaxed">Sell individual seats up to capacity. Group tours, classes, events.</p>
        </div>
        <div className="rounded-lg bg-gray-50 p-4">
          <div className="text-sm font-semibold text-gray-900 mb-1">Per-asset (exclusive)</div>
          <p className="text-xs text-gray-600 leading-relaxed">One booking takes the whole asset. Boat charters, vehicle rentals.</p>
        </div>
      </div>
    </Card>
    <Card className="mt-3">
      <div className="text-sm font-medium text-gray-900 mb-2">Pricing models</div>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        Every service picks its own pricing model — mix and match across your catalog.
      </p>
      <div className="space-y-3">
        {[
          ['Flat price', 'One price for the whole booking, regardless of party size. Charters, exclusive rentals.'],
          ['Per-person', 'Price × party size, straight from the widget. Tours, classes, group activities.'],
          ['Day-of-week & seasonal rates', 'Weekend premiums and high-season pricing applied automatically by date.'],
          ['Deposits', 'Collect a fixed amount or % now — the balance is charged later via a payment link.'],
          ['Add-ons', 'Fixed-amount or %-of-base extras: photos, fuel, captain, equipment.'],
          ['Taxes, fees & gratuity', 'Sales tax, service fees, and optional gratuity — each a per-service toggle.'],
        ].map(([t, d]) => (
          <div key={t} className="flex gap-3">
            <Check className="w-4 h-4 mt-1 text-emerald-600 flex-shrink-0" />
            <div>
              <div className="text-sm font-medium text-gray-900">{t}</div>
              <div className="text-xs text-gray-500 mt-0.5">{d}</div>
            </div>
          </div>
        ))}
      </div>
      <PathHint>Deck → Services → open a service → Pricing</PathHint>
    </Card>
    <Card className="mt-3">
      <div className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-violet-600" />
        AI-assisted creation
      </div>
      <p className="text-sm text-gray-600 leading-relaxed">
        The Create Service wizard can draft your description and suggest pricing based
        on similar services in your niche. AI drafts, you approve. Custom intake
        questions per service are set here too. Changes save instantly and hit your
        booking widget immediately.
      </p>
      <PathHint>Deck → Services → New service</PathHint>
    </Card>
  </div>
);

const ScheduleMineContent = () => (
  <div>
    <SectionHeader icon={CalendarCheck} title="My Schedule" subtitle="Just your trips." />
    <Card>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        A filtered view of the calendar showing only bookings assigned to you. Tap a
        trip for the brief, customer contact info, and a one-tap check-in.
      </p>
      <ul className="space-y-2">
        <Bullet>Today, week, and month views</Bullet>
        <Bullet>Tap a trip for service details and customer info</Bullet>
        <Bullet>One-tap check-in from the calendar</Bullet>
      </ul>
      <PathHint>Deck → My Schedule</PathHint>
    </Card>
  </div>
);

const BookingsContent = () => (
  <div>
    <SectionHeader icon={Calendar} title="Bookings & Calendar" subtitle="Every trip on one timeline." />
    <Card>
      <ul className="space-y-2">
        <Bullet>Day / week / month views</Bullet>
        <Bullet>Filter by service, asset, or team member</Bullet>
        <Bullet>Drag to reschedule — emails the customer automatically</Bullet>
        <Bullet>Color-coded per service</Bullet>
        <Bullet>Subscribe via iCal/Google (read-only feed)</Bullet>
      </ul>
      <PathHint>Deck → Bookings</PathHint>
    </Card>
  </div>
);

const AssignmentsContent = () => (
  <div>
    <SectionHeader icon={TicketCheck} title="Assignments" subtitle="Claim the trips you're qualified for." />
    <Card>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        When a booking needs staff, it shows up here. Qualified team members claim it
        directly, or an admin assigns from the booking. Once claimed, it moves to your
        "My Schedule".
      </p>
      <ul className="space-y-2">
        <Bullet>Unclaimed trips show first; claimed trips slide down</Bullet>
        <Bullet>Every card shows service, asset, time, and party size</Bullet>
        <Bullet>You only see what your role qualifies you for</Bullet>
      </ul>
      <PathHint>Deck → Assignments</PathHint>
    </Card>
  </div>
);

const FlowsContent = () => (
  <div>
    <SectionHeader icon={Link2} title="Booking Flows" subtitle="How customers reach you." />
    <Card>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        A <strong>Flow</strong> is a configured booking widget — pick the services it
        shows, set the branding, and surface it three ways:
      </p>
      <div className="grid sm:grid-cols-3 gap-3 mb-2">
        <div className="rounded-lg bg-gray-50 p-3">
          <Code className="w-4 h-4 text-gray-700 mb-1.5" />
          <div className="text-sm font-semibold text-gray-900">Embed</div>
          <div className="text-xs text-gray-500 mt-0.5">One snippet on your site</div>
        </div>
        <div className="rounded-lg bg-gray-50 p-3">
          <Link2 className="w-4 h-4 text-gray-700 mb-1.5" />
          <div className="text-sm font-semibold text-gray-900">Direct link</div>
          <div className="text-xs text-gray-500 mt-0.5">DMs, bios, emails</div>
        </div>
        <div className="rounded-lg bg-gray-50 p-3">
          <QrCode className="w-4 h-4 text-gray-700 mb-1.5" />
          <div className="text-sm font-semibold text-gray-900">QR code</div>
          <div className="text-xs text-gray-500 mt-0.5">Print on signage</div>
        </div>
      </div>
    </Card>
    <Card className="mt-3">
      <div className="text-sm font-medium text-gray-900 mb-2">Launching a flow</div>
      <div className="space-y-3">
        {[
          ['1 · Create the flow', 'Name it, pick which services it sells — one flow per landing page, ad, or partner works great.'],
          ['2 · Style it', 'Choose a card style — Classic (image-on-top), Flat (minimal), Premium (full-bleed photo), or Ticket (perforated stub).'],
          ['3 · Put it in front of guests', 'Copy the embed snippet into your website, drop the direct link into DMs and bios, or download the QR for dock signage and flyers.'],
          ['4 · Watch it convert', 'Every flow visit and click is tracked — the funnel shows up in Conversions, and campaign links pointing at a flow trace bookings back to the campaign.'],
        ].map(([t, d]) => (
          <div key={t} className="flex gap-3">
            <Check className="w-4 h-4 mt-1 text-emerald-600 flex-shrink-0" />
            <div>
              <div className="text-sm font-medium text-gray-900">{t}</div>
              <div className="text-xs text-gray-500 mt-0.5">{d}</div>
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-500 leading-relaxed mt-4">
        Tip: flows are direct booking pages — they make perfect landing spots for
        HELM Intelligence campaigns.
      </p>
      <PathHint>Deck → Flows</PathHint>
    </Card>
  </div>
);

const PaymentsContent = () => (
  <div>
    <SectionHeader icon={CreditCard} title="Payments" subtitle="Stripe Connect, direct to you." />
    <Card>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        Funds land in <strong>your</strong> Stripe account on your normal payout schedule.
        BookingFlows never holds your money — we use Stripe Connect Direct Charges.
      </p>
      <ul className="space-y-2">
        <Bullet>Cards, Apple Pay, Google Pay — crypto if your Stripe supports it</Bullet>
        <Bullet>Deposits (fixed or %) with later balance-due links</Bullet>
        <Bullet>Refunds processed directly inside the booking</Bullet>
        <Bullet>Multi-currency — Stripe minimums enforced per-currency</Bullet>
        <Bullet>Platform & partner fees split server-side at charge time</Bullet>
      </ul>
      <PathHint>Purser → Overview</PathHint>
    </Card>
  </div>
);

const ProfitLossContent = () => (
  <div>
    <SectionHeader icon={PieChart} title="Profit & Loss" subtitle="Month over month." />
    <Card>
      <ul className="space-y-2">
        <Bullet>Revenue and expense totals side-by-side</Bullet>
        <Bullet>Category breakdown (services, add-ons, fees, refunds)</Bullet>
        <Bullet>Comparison vs. previous period</Bullet>
        <Bullet>CSV export for your accountant</Bullet>
      </ul>
      <PathHint>Purser → P&L</PathHint>
    </Card>
  </div>
);

const RevenueContent = () => (
  <div>
    <SectionHeader icon={TrendingUp} title="Revenue" subtitle="Where the money came from." />
    <Card>
      <ul className="space-y-2">
        <Bullet>Filter by service, asset, channel, or partner</Bullet>
        <Bullet>Actual vs projected revenue with refund net-out</Bullet>
        <Bullet>Break out by gift cards, discounts, and add-ons</Bullet>
      </ul>
      <PathHint>Purser → Revenue</PathHint>
    </Card>
  </div>
);

const ExpensesContent = () => (
  <div>
    <SectionHeader icon={Receipt} title="Expenses" subtitle="Log costs, keep your books clean." />
    <Card>
      <ul className="space-y-2">
        <Bullet>Log expenses with category, vendor, and date</Bullet>
        <Bullet>Attach receipts (image or PDF)</Bullet>
        <Bullet>Recurring expense support</Bullet>
        <Bullet>CSV export at tax time</Bullet>
      </ul>
      <PathHint>Purser → Expenses</PathHint>
    </Card>
  </div>
);

const PartnersContent = () => (
  <div>
    <SectionHeader icon={UserPlus} title="Partners" subtitle="Split revenue automatically." />
    <Card>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        Connect a partner to your services and the platform splits their share
        server-side at payment — they get paid directly via Stripe Connect.
      </p>
      <ul className="space-y-2">
        <Bullet>Revenue partners — tied to specific services or all of them</Bullet>
        <Bullet>Affiliates — paid only on referred bookings via referral link</Bullet>
        <Bullet>Each partner connects their own Stripe — they manage their own payouts</Bullet>
      </ul>
      <PathHint>Purser → Partners</PathHint>
    </Card>
  </div>
);

const BrandingContent = () => (
  <div>
    <SectionHeader icon={Sliders} title="Branding" subtitle="Make it look like you, not us." />
    <Card>
      <ul className="space-y-2">
        <Bullet>Upload logo and accent color — applied across widgets, emails, tickets</Bullet>
        <Bullet>Customize email templates with merge fields</Bullet>
        <Bullet>Hide BookingFlows branding entirely (white-label)</Bullet>
      </ul>
      <PathHint>Customers → Branding</PathHint>
    </Card>
  </div>
);

const WaiversContent = () => (
  <div>
    <SectionHeader icon={Shield} title="Waivers" subtitle="Legally captured signatures." />
    <Card>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        Customers sign on their own device after booking. Every signature captures the
        legal name, drawn signature, timestamp (UTC), IP address, device, and the exact
        waiver version they agreed to.
      </p>
      <ul className="space-y-2">
        <Bullet>Custom waiver text per organization</Bullet>
        <Bullet>Version tracking — old signatures stay tied to the version signed</Bullet>
        <Bullet>Required before check-in if you toggle it on</Bullet>
        <Bullet>Export any signature as PDF for legal records</Bullet>
        <Bullet>UAE organizations sign bilingually in English & Arabic</Bullet>
      </ul>
      <PathHint>Customers → Waivers</PathHint>
    </Card>
    <Card className="mt-3">
      <div className="text-sm font-medium text-gray-900 mb-2">Three ways guests sign</div>
      <div className="space-y-3">
        {[
          ['From their ticket', 'Every confirmation email links to the customer ticket page, which carries its own "Sign waiver" button — each guest in the group signs from their own phone before they ever reach you.'],
          ['Walk-up link & QR', 'Your public signing page lives at /sign/yourcompany — copy the link or print the QR at the dock for walk-ins and day-of guests without a booking.'],
          ['At check-in', 'Staff can pull the waiver up on any device and hand it over. Signature status shows right on the manifest booking rows so you know who still owes one.'],
        ].map(([t, d]) => (
          <div key={t} className="flex gap-3">
            <Check className="w-4 h-4 mt-1 text-emerald-600 flex-shrink-0" />
            <div>
              <div className="text-sm font-medium text-gray-900">{t}</div>
              <div className="text-xs text-gray-500 mt-0.5">{d}</div>
            </div>
          </div>
        ))}
      </div>
      <PathHint>Customers → Waivers → Walk-up Link (preview shows exactly what guests see)</PathHint>
    </Card>
  </div>
);

const ReviewsContent = () => (
  <div>
    <SectionHeader icon={Star} title="Reviews" subtitle="Drive social proof on autopilot." />
    <Card>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        Reviews come in two ways: the post-trip Thank-You email asks for one
        automatically, and customers can leave them from their ticket page. High-rated
        reviews surface on your marketplace profile.
      </p>
      <ul className="space-y-2">
        <Bullet>Auto-prompt via the post-trip email</Bullet>
        <Bullet>Public reviews on your marketplace listing</Bullet>
        <Bullet>Reply privately or publicly</Bullet>
      </ul>
      <PathHint>Customers → Reviews</PathHint>
    </Card>
  </div>
);

const CheckInContent = () => (
  <div>
    <SectionHeader icon={QrCode} title="Check-In" subtitle="Get guests on board fast." />
    <Card>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="rounded-lg bg-gray-50 p-3">
          <div className="text-sm font-semibold text-gray-900">QR scanner</div>
          <div className="text-xs text-gray-500 mt-0.5">Barcode gun reads customer ticket</div>
        </div>
        <div className="rounded-lg bg-gray-50 p-3">
          <div className="text-sm font-semibold text-gray-900">Phone camera</div>
          <div className="text-xs text-gray-500 mt-0.5">Open the tab, point, scan</div>
        </div>
        <div className="rounded-lg bg-gray-50 p-3">
          <div className="text-sm font-semibold text-gray-900">Manual</div>
          <div className="text-xs text-gray-500 mt-0.5">Tap check-in inside the booking</div>
        </div>
        <div className="rounded-lg bg-gray-50 p-3">
          <div className="text-sm font-semibold text-gray-900">Self check-in</div>
          <div className="text-xs text-gray-500 mt-0.5">Customer scans your venue QR</div>
        </div>
      </div>
      <p className="text-sm text-gray-600 leading-relaxed mt-4">
        Every check-in is timestamped on the calendar, the customer ticket, and Relations
        history. No-shows are tracked separately so you can spot patterns.
      </p>
      <PathHint>Customers → Check-In</PathHint>
    </Card>
  </div>
);

const RelationsContent = () => (
  <div>
    <SectionHeader icon={Users} title="Relations" subtitle="Customers, leads, and lifetime value." />
    <Card>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        Every booker rolls up into a single contact with booking history, lifetime
        spend, waivers signed, and tags. One contact per email — even if they used
        different names.
      </p>
      <ul className="space-y-2">
        <Bullet>Filter by status: lead, customer, returning, VIP</Bullet>
        <Bullet>Lifetime value, last-booking date, no-show rate</Bullet>
        <Bullet>Bulk export to CSV for email marketing</Bullet>
        <Bullet>Lead capture from the widget for cart abandonment recovery</Bullet>
      </ul>
      <PathHint>Customers → Relations</PathHint>
    </Card>
  </div>
);

const DiscountsContent = () => (
  <div>
    <SectionHeader icon={Tag} title="Discounts" subtitle="Promo codes, smartly capped." />
    <Card>
      <ul className="space-y-2">
        <Bullet>Percent or fixed-amount codes</Bullet>
        <Bullet>Usage caps and expiry dates</Bullet>
        <Bullet>Per-service eligibility and minimum order amount</Bullet>
        <Bullet>Affiliate variant — commission tracked automatically</Bullet>
        <Bullet>Attach a code to an Intelligence campaign to track what it brings back</Bullet>
      </ul>
      <PathHint>Customers → Discounts</PathHint>
    </Card>
  </div>
);

const GiftCardsContent = () => (
  <div>
    <SectionHeader icon={Gift} title="Gift Cards" subtitle="Sell credit, redeem at checkout." />
    <Card>
      <ul className="space-y-2">
        <Bullet>Customers buy gift cards from your public storefront</Bullet>
        <Bullet>Codes redeem inside the booking widget</Bullet>
        <Bullet>Partial redemption supported — remaining balance carries over</Bullet>
        <Bullet>Stripe minimum enforced so the card never fully zeroes a payment</Bullet>
      </ul>
      <PathHint>Customers → Gift Cards</PathHint>
    </Card>
  </div>
);

const NotificationsContent = () => (
  <div>
    <SectionHeader icon={Bell} title="Notifications" subtitle="Email + SMS, on autopilot." />
    <Card>
      <div className="space-y-3">
        {[
          ['Booking confirmation', 'Sent the moment payment succeeds — magic link to the ticket included.'],
          ['Reminder (24h before)', 'Cron-triggered. Respects your toggle here.'],
          ['Reschedule / cancel', 'Auto-sent when the booking changes on the calendar.'],
          ['Thank-you + review', 'Sent after the experience. Drives Google/Yelp reviews.'],
          ['Owner alerts', 'You get pinged on new bookings, refunds, and lead drop-offs.'],
        ].map(([title, desc]) => (
          <div key={title} className="flex gap-3">
            <Check className="w-4 h-4 mt-1 text-emerald-600 flex-shrink-0" />
            <div>
              <div className="text-sm font-medium text-gray-900">{title}</div>
              <div className="text-xs text-gray-500 mt-0.5">{desc}</div>
            </div>
          </div>
        ))}
      </div>
      <PathHint>Customers → Notifications</PathHint>
    </Card>
    <Card className="mt-3">
      <div className="text-sm font-medium text-gray-900 mb-2">Waiver reminders — dual-channel</div>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        Guests who haven't signed get nudged automatically before the trip — by email
        AND text — so nobody shows up at the dock with paperwork pending.
      </p>
      <ul className="space-y-2">
        <Bullet>Reminder sequence escalates as the trip gets closer</Bullet>
        <Bullet>Signing verifies the guest's email or phone with a one-time code (OTP)</Bullet>
        <Bullet>Verified contact info lands in Relations — and feeds your Intelligence audiences (marketing opt-in always respected)</Bullet>
      </ul>
    </Card>
  </div>
);

const MarketplaceContent = () => (
  <div>
    <SectionHeader icon={Store} title="Marketplace" subtitle="Your public BookingFlows profile." />
    <Card>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        A destination page guests can browse, share, and book straight from. Pulled
        automatically from your branding and active services — no separate page to
        maintain.
      </p>
      <ul className="space-y-2">
        <Bullet>Auto-uses your logo, accent color, and active services</Bullet>
        <Bullet>Toggle which services appear on the public listing</Bullet>
        <Bullet>Direct link you can share, plus search-friendly SEO</Bullet>
      </ul>
      <PathHint>Marketing → Marketplace</PathHint>
    </Card>
  </div>
);

const BlogContent = () => (
  <div>
    <SectionHeader icon={PenTool} title="SEO Blog" subtitle="AI-drafted articles for search visibility." />
    <Card>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        AI generates SEO-tuned posts built from your services and location. Every draft
        is fully editable — you choose what publishes and where.
      </p>
      <ul className="space-y-2">
        <Bullet>Topic suggestions based on your niche and city</Bullet>
        <Bullet>Drafts include meta description and structured headings</Bullet>
        <Bullet>Publish to your site, status: draft / published / archived</Bullet>
        <Bullet>Posts link straight to your booking flows — search traffic lands on a bookable page</Bullet>
      </ul>
      <PathHint>Marketing → SEO</PathHint>
    </Card>
    <Card className="mt-3">
      <div className="text-sm font-medium text-gray-900 mb-2">Getting the most out of it</div>
      <ul className="space-y-2">
        <Bullet>Publish consistently — a post or two a month compounds in search over time</Bullet>
        <Bullet>Share each post to your socials for an immediate traffic bump</Bullet>
        <Bullet>With the Beacon installed, Website Analytics shows which posts actually drive booking intent</Bullet>
      </ul>
    </Card>
  </div>
);

const ConversionsContent = () => (
  <div>
    <SectionHeader icon={Target} title="Integrations" subtitle="Track what's working — and connect your other tools." />
    <Card>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        See how many widget viewers convert, how big the average booking is, and which
        channels drive the most bookings. Tie Google Ads conversion events so your
        ad spend is measurable — and push booking events into 8,000+ apps via Zapier.
      </p>
      <ul className="space-y-2">
        <Bullet>Funnel: viewers → started → completed → checked-in</Bullet>
        <Bullet>Channel breakdown (organic, social, referral, partner)</Bullet>
        <Bullet>GA4, Google Ads and Meta Pixel setup — every booking fires a conversion event</Bullet>
        <Bullet>Zapier — generate an API key or paste a Catch Hook URL to trigger Zaps on confirmed bookings and new leads</Bullet>
      </ul>
      <PathHint>Marketing → Integrations</PathHint>
    </Card>
    <Card className="mt-3">
      <div className="text-sm font-medium text-gray-900 mb-2">The Beacon & Chrome extension</div>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        The BookingFlows Beacon is a tiny tag on your website that streams visitor
        events into HELM Intelligence — its live status shows on this tab. Want proof
        it's working? Install our Chrome extension, open any page of your site, and
        watch the events fire in real time.
      </p>
      <a
        href={EXTENSION_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="help-conversions-extension-link"
        className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 transition-colors"
      >
        <BeaconIcon className="w-4 h-4" />
        BF Beacon Tender — Chrome Web Store
      </a>
      <PathHint>Marketing → Integrations → Verify your setup</PathHint>
    </Card>
  </div>
);

const IntelligenceContent = () => (
  <div>
    <SectionHeader icon={Sparkles} title="HELM Intelligence Suite" subtitle="Your data & marketing command center — built automatically as you operate." />
    <Card>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">
        Every booking, page view, waiver and customer interaction quietly feeds the
        BookingFlows Beacon, which aggregates it into live analytics and ready-to-use
        audiences. Nothing to configure — the more you use BookingFlows, the richer it gets.
      </p>
      <ul className="space-y-2">
        <Bullet><strong>Analytics</strong> — live revenue, bookings, and funnel performance across your services.</Bullet>
        <Bullet><strong>Audiences</strong> — the Beacon groups your customers/leads into segments you can market to.</Bullet>
        <Bullet><strong>Campaigns</strong> — run email/ad campaigns to those audiences, and attach a discount code to drive repeat bookings.</Bullet>
        <Bullet><strong>Reports</strong> — asset & team performance so you know what's earning and what to buy next.</Bullet>
        <Bullet><strong>Access control</strong> — grant specific team members access to the suite from Roles.</Bullet>
        <Bullet><strong>Hands-on tour</strong> — the ✨ sparkle tour walks you through drafting real ads with AI and test-sending them to your own inbox. Starter ad credit included.</Bullet>
      </ul>
      <PathHint>Sidebar → Intelligence</PathHint>
    </Card>
  </div>
);

const AskBosunContent = () => (
  <div>
    <SectionHeader icon={Sparkles} title="Ask Bosun & Guided Tours" subtitle="Your first mate — help that knows the ship." />
    <Card>
      <div className="flex items-start gap-4 mb-4">
        <img src="/bosun-avatar.png" alt="Bosun" className="w-14 h-14 rounded-full ring-2 ring-[#F5B92E]/50 flex-shrink-0" />
        <p className="text-sm text-gray-600 leading-relaxed">
          <strong className="text-gray-900">Ask Bosun</strong> is a context-aware AI
          assistant built into the platform. Open the ✨ sparkle tab on the right edge
          of any screen and hail him — ask in plain words and he'll point you to the
          exact tab and steps.
        </p>
      </div>
      <ul className="space-y-2">
        <Bullet>Knows which tab you're on and answers in that context</Bullet>
        <Bullet>Try: "How do I add a team member?" · "Why isn't my Beacon live?" · "Where do I set deposits?"</Bullet>
        <Bullet>Up to 10 questions per hour</Bullet>
        <Bullet>Conversations are yours — wipe the chat memory anytime from the panel</Bullet>
        <Bullet>Bosun guides, you steer — he never changes your data</Bullet>
      </ul>
      <PathHint>✨ sparkle tab (right edge) → Ask Bosun</PathHint>
    </Card>
    <Card className="mt-3">
      <div className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-2">
        <Rocket className="w-4 h-4 text-[#1F2A5C]" />
        Guided tours
      </div>
      <div className="space-y-3">
        {[
          ['Basic tour', 'The core loop, deck by deck — assets & crew rules first, then services, bookings, waivers and your walk-up signing link. Auto-plays on your first visit.'],
          ['Advanced tour', 'Reports, the Settings engine room (calendar sync, 2FA, passkeys, API keys, Log book), the Beacon — then hands off into the Intelligence Suite.'],
          ['Intelligence Suite tour', "Hands-on: it has you click \"Draft ad variations with AI\" and watch real ads appear, then test-send them to your own inbox before anything launches."],
          ['How-to guides', "Bite-size, hands-on task walkthroughs — set up your crew, add an asset, create your first service and flow, book like a customer, run a promo. In the ✨ menu under \"How-to Guides\" — the Intelligence Suite has its own set (Chartroom, Beacon install & verify, campaigns)."],
        ].map(([t, d]) => (
          <div key={t} className="flex gap-3">
            <Check className="w-4 h-4 mt-1 text-emerald-600 flex-shrink-0" />
            <div>
              <div className="text-sm font-medium text-gray-900">{t}</div>
              <div className="text-xs text-gray-500 mt-0.5">{d}</div>
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-500 leading-relaxed mt-4">
        Replay any tour anytime from the ✨ sparkle tab — they're role-aware, so team
        members only see stops for tabs they can access.
      </p>
    </Card>
  </div>
);

const SettingsContent = () => (
  <div>
    <SectionHeader icon={Settings} title="Settings" subtitle="Account, security, configuration." />
    <Card>
      <div className="grid sm:grid-cols-2 gap-3">
        {[
          ['Profile', 'Business name, contact, timezone'],
          ['Calendar', 'Business hours, blackout dates, iCal feed'],
          ['Integrations', 'Stripe, email, calendar sync'],
          ['2FA & passkeys', 'Stronger logins for you and your team'],
          ['Webhooks', 'Outbound HTTPS events'],
          ['API Keys', 'Programmatic access'],
          ['Log book', 'Every action stamped with who and when'],
          ['Danger zone', 'Delete data, reset billing — irreversible'],
        ].map(([t, d]) => (
          <div key={t} className="rounded-lg bg-gray-50 p-3">
            <div className="text-sm font-medium text-gray-900">{t}</div>
            <div className="text-xs text-gray-500 mt-0.5">{d}</div>
          </div>
        ))}
      </div>
      <PathHint>Sidebar → Settings</PathHint>
    </Card>
  </div>
);

const MyProfileContent = () => (
  <div>
    <SectionHeader icon={UserCheck} title="My Profile" subtitle="Your personal account settings." />
    <Card>
      <ul className="space-y-2">
        <Bullet>Update your name, email, and contact info</Bullet>
        <Bullet>Change your password or add a passkey</Bullet>
        <Bullet>Subscribe to your trip calendar via iCal/Google</Bullet>
        <Bullet>See your activity log</Bullet>
      </ul>
      <PathHint>Sidebar → My Profile</PathHint>
    </Card>
  </div>
);

/* ─────────────────────────────────────────────────────────────────────────
 * Tab definitions — keyed by dashboard navigation IDs.
 * ────────────────────────────────────────────────────────────────────── */

export const TAB_HELP = {
  home: { label: 'Dashboard', icon: LayoutDashboard, blurb: "Today's bookings, revenue, and leads.", component: HomeContent },
  roles: { label: 'Roles', icon: Shield, blurb: 'Define positions and per-tab permissions.', component: RolesContent },
  teamMembers: { label: 'Team', icon: Users, blurb: 'Invite staff and manage their access.', component: TeamContent },
  assets: { label: 'Assets', icon: Package, blurb: 'Boats, venues, vehicles — your bookable inventory.', component: AssetsContent },
  services: { label: 'Services', icon: Zap, blurb: 'Build the bookable experiences customers buy.', component: ServicesContent },
  mySchedule: { label: 'My Schedule', icon: CalendarCheck, blurb: 'Just the trips assigned to you.', component: ScheduleMineContent },
  schedule: { label: 'Bookings', icon: Calendar, blurb: 'Every trip on the calendar.', component: BookingsContent },
  assignments: { label: 'Assignments', icon: TicketCheck, blurb: 'Claim trips you can run.', component: AssignmentsContent },
  flows: { label: 'Flows', icon: Sparkles, blurb: 'Your embeddable booking widgets.', component: FlowsContent },
  payments: { label: 'Payments', icon: CreditCard, blurb: 'Stripe status, payouts, and balances.', component: PaymentsContent },
  profitLoss: { label: 'P&L', icon: PieChart, blurb: 'Profit and loss, month over month.', component: ProfitLossContent },
  revenue: { label: 'Revenue', icon: TrendingUp, blurb: 'Where the money came from.', component: RevenueContent },
  expenses: { label: 'Expenses', icon: Receipt, blurb: 'Costs, receipts, and tax-ready exports.', component: ExpensesContent },
  partners: { label: 'Partners', icon: UserPlus, blurb: 'Split revenue with partners automatically.', component: PartnersContent },
  branding: { label: 'Branding', icon: Sliders, blurb: 'Logo, color, email and ticket styling.', component: BrandingContent },
  waivers: { label: 'Waivers', icon: Shield, blurb: 'Legally captured digital signatures.', component: WaiversContent },
  reviews: { label: 'Reviews', icon: Star, blurb: 'Auto-collected social proof.', component: ReviewsContent },
  checkin: { label: 'Check-In', icon: QrCode, blurb: 'Four ways to get guests on board.', component: CheckInContent },
  relations: { label: 'Relations', icon: Users, blurb: 'Customer CRM with lifetime value.', component: RelationsContent },
  discounts: { label: 'Discounts', icon: Tag, blurb: 'Promo codes and affiliate codes.', component: DiscountsContent },
  giftCards: { label: 'Gift Cards', icon: Gift, blurb: 'Sell credit, redeem at checkout.', component: GiftCardsContent },
  notifications: { label: 'Notifications', icon: Bell, blurb: 'Email + SMS automations.', component: NotificationsContent },
  marketplace: { label: 'Marketplace', icon: Store, blurb: 'Your public BookingFlows profile.', component: MarketplaceContent },
  blog: { label: 'SEO', icon: PenTool, blurb: 'AI-drafted articles for organic search.', component: BlogContent },
  conversions: { label: 'Conversions', icon: Target, blurb: 'Funnel, channels, and ad tracking.', component: ConversionsContent },
  intelligence: { label: 'Intelligence', icon: Sparkles, blurb: 'Analytics, audiences & marketing campaigns.', component: IntelligenceContent },
};

export const ADMIN_EXTRA_HELP = {
  askBosun: { label: 'Ask Bosun & Tours', icon: Sparkles, component: AskBosunContent },
  settingsHelp: { label: 'Settings', icon: Settings, component: SettingsContent },
};

export const TEAM_EXTRA_HELP = {
  askBosun: { label: 'Ask Bosun & Tours', icon: Sparkles, component: AskBosunContent },
  account: { label: 'My Profile', icon: UserCheck, component: MyProfileContent },
};
